import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FiSend, FiMail, FiLinkedin, FiPhone, FiUser, FiChevronDown,
  FiChevronUp, FiRefreshCw, FiArrowRight, FiFileText,
} from 'react-icons/fi';
import { API_URL } from '../../../utils/authUtils';

/**
 * OutreachPanel - Handles STAGED and SENT states.
 *
 * Allows staff to:
 * - View target info and recommended channel
 * - Compose outreach using templates
 * - Send outreach (STAGED -> SENT)
 * - Record responses to outreach attempts
 * - Proceed to consent when positive response received
 */

const CHANNELS = [
  { value: 'email', label: 'Email', icon: FiMail },
  { value: 'linkedin', label: 'LinkedIn', icon: FiLinkedin },
  { value: 'phone', label: 'Phone', icon: FiPhone },
  { value: 'in_person', label: 'In Person', icon: FiUser },
];

const RESPONSE_TYPES = [
  { value: 'positive', label: 'Positive', color: 'bg-green-100 text-green-700 hover:bg-green-200' },
  { value: 'negative', label: 'Negative', color: 'bg-red-100 text-red-700 hover:bg-red-200' },
  { value: 'no_response', label: 'No Response', color: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' },
  { value: 'defer', label: 'Defer', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
  { value: 'bounce', label: 'Bounce', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
];

const getErrorMessage = (err, defaultMsg) => {
  if (err.response?.data) {
    const detail = err.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
  }
  return defaultMsg;
};

const OutreachPanel = ({ requestDetail, requestId, onRequestUpdated, authHeaders }) => {
  // Outreach compose state
  const [channel, setChannel] = useState('email');
  const [subject, setSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Outreach history
  const [outreachAttempts, setOutreachAttempts] = useState([]);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [expandedAttempt, setExpandedAttempt] = useState(null);

  // Response recording
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseType, setResponseType] = useState('');
  const [responseNotes, setResponseNotes] = useState('');
  const [recordingResponse, setRecordingResponse] = useState(false);

  // Delivery channel recommendation
  const [deliveryChannel, setDeliveryChannel] = useState(null);

  // Test mode - redirects email to admin instead of target
  const [testMode, setTestMode] = useState(false);

  // Request reference panel
  const [showRequestRef, setShowRequestRef] = useState(false);

  // General state
  const [error, setError] = useState(null);
  const [showComposer, setShowComposer] = useState(requestDetail.status === 'STAGED');

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/staff/brokerage/templates`, authHeaders());
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  }, [authHeaders]);

  // Fetch outreach history
  const fetchOutreach = useCallback(async () => {
    setOutreachLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/outreach`,
        authHeaders()
      );
      setOutreachAttempts(res.data.items || []);
    } catch (err) {
      console.error('Failed to load outreach history:', err);
    } finally {
      setOutreachLoading(false);
    }
  }, [requestId, authHeaders]);

  // Fetch delivery channel recommendation
  const fetchDeliveryChannel = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/delivery-channel`,
        authHeaders()
      );
      setDeliveryChannel(res.data);
      if (res.data.recommended_channel) {
        const ch = res.data.recommended_channel.toLowerCase();
        // Map backend channel types to our UI values
        if (ch.startsWith('email')) setChannel('email');
        else if (ch.startsWith('linkedin')) setChannel('linkedin');
        else if (ch.startsWith('phone')) setChannel('phone');
      }
    } catch (err) {
      console.error('Failed to load delivery channel:', err);
    }
  }, [requestId, authHeaders]);

  useEffect(() => {
    fetchTemplates();
    fetchOutreach();
    fetchDeliveryChannel();
  }, [fetchTemplates, fetchOutreach, fetchDeliveryChannel]);

  // Preview template with real data
  const previewTemplate = async () => {
    if (!selectedTemplateId) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/staff/brokerage/templates/${selectedTemplateId}/preview`,
        {
          request_id: requestId,
          target_person_id: requestDetail.target_person_id,
          sample_data: false,
        },
        authHeaders()
      );
      if (res.data.rendered_subject || res.data.subject) setSubject(res.data.rendered_subject || res.data.subject);
      if (res.data.rendered_body || res.data.body) setMessageBody(res.data.rendered_body || res.data.body);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to preview template'));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Send outreach
  const sendOutreach = async () => {
    if (!messageBody.trim()) {
      setError('Message body is required');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/outreach`,
        { channel, subject: subject || null, message_body: messageBody, test_mode: testMode },
        authHeaders()
      );
      const data = res.data;
      setSubject('');
      setMessageBody('');
      setSelectedTemplateId('');
      setShowComposer(false);
      await fetchOutreach();
      onRequestUpdated();
      // Show email delivery feedback
      if (channel === 'email') {
        if (data.email_sent) {
          setError(null);
          alert(`Outreach email sent to ${data.email_recipient}`);
        } else if (data.email_error) {
          setError(`Outreach recorded, but email could not be sent: ${data.email_error}`);
        }
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to send outreach'));
    } finally {
      setSending(false);
    }
  };

  // Record response to an outreach attempt
  const handleRecordResponse = async (outreachId) => {
    if (!responseType) {
      setError('Select a response type');
      return;
    }
    setRecordingResponse(true);
    setError(null);
    try {
      await axios.patch(
        `${API_URL}/api/staff/brokerage/outreach/${outreachId}/response`,
        { response_type: responseType, response_notes: responseNotes || null },
        authHeaders()
      );
      setRespondingTo(null);
      setResponseType('');
      setResponseNotes('');
      await fetchOutreach();
      onRequestUpdated();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to record response'));
    } finally {
      setRecordingResponse(false);
    }
  };

  // Transition to AWAITING (for consent flow)
  const proceedToConsent = async () => {
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/transition`,
        { target_status: 'AWAITING', row_version: requestDetail.row_version },
        authHeaders()
      );
      onRequestUpdated();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to proceed to consent'));
    }
  };

  // Check if any positive response exists
  const hasPositiveResponse = outreachAttempts.some((a) => a.response_type === 'positive');

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-1">Outreach</h3>
          <p className="text-xs text-gray-500">
            {requestDetail.status === 'STAGED'
              ? 'Compose and send outreach to the selected target.'
              : 'Track outreach responses and send follow-ups.'}
          </p>
        </div>
        {!showComposer && (
          <button
            onClick={() => setShowComposer(true)}
            className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1"
          >
            <FiSend className="w-3 h-3" /> {requestDetail.status === 'STAGED' ? 'Compose Outreach' : 'Send Follow-up'}
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Target Info Card */}
      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center">
            <span className="text-purple-600 font-semibold text-sm">
              {(requestDetail.target_name || 'T')[0].toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800">{requestDetail.target_name || 'Unknown Target'}</p>
            {deliveryChannel && (
              <p className="text-xs text-gray-500">
                Recommended: <span className="font-medium">{deliveryChannel.recommended_channel}</span>
                {deliveryChannel.channel_details?.email && ` (${deliveryChannel.channel_details.email})`}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Proceed to Consent (if positive response) */}
      {hasPositiveResponse && requestDetail.valid_transitions?.includes('AWAITING') && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-green-800">Positive response received!</p>
            <p className="text-xs text-green-600">Ready to proceed to consent request.</p>
          </div>
          <button
            onClick={proceedToConsent}
            className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
          >
            <FiArrowRight className="w-3.5 h-3.5" /> Proceed to Consent
          </button>
        </div>
      )}

      {/* Outreach Composer */}
      {showComposer && (
        <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-indigo-800">Compose Outreach</p>
            <button onClick={() => setShowComposer(false)} className="text-xs text-indigo-600 hover:text-indigo-700">
              Cancel
            </button>
          </div>

          {/* Original request reference */}
          <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
            <button
              onClick={() => setShowRequestRef(!showRequestRef)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              <span>Original Request Details</span>
              {showRequestRef ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
            </button>
            {showRequestRef && (
              <div className="px-3 pb-3 space-y-2 text-xs border-t border-gray-100">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-2">
                  <div>
                    <span className="text-gray-500">Requester:</span>{' '}
                    <span className="font-medium text-gray-800">{requestDetail.requester_name || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Type:</span>{' '}
                    <span className="font-medium text-gray-800">
                      {({'PEER_MID_LEVEL':'Peer Networking','PEER_SENIOR':'Peer Networking (Senior)','MENTORSHIP_UP':'Seek Mentorship','MENTORSHIP_HIGH':'Offer Mentorship','BOARD_PATHWAY':'Board Pathway','HBCU_MENTORSHIP':'HBCU Mentorship'})[requestDetail.request_type] || (requestDetail.request_type || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Target:</span>{' '}
                    <span className="font-medium text-gray-800">{requestDetail.target_name || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Priority:</span>{' '}
                    <span className="font-medium text-gray-800">
                      {requestDetail.priority_score != null ? Number(requestDetail.priority_score).toFixed(2) : '—'}
                    </span>
                  </div>
                </div>
                {requestDetail.request_payload?.topics_of_interest?.length > 0 && (
                  <div>
                    <span className="text-gray-500">Topics:</span>{' '}
                    <span className="text-gray-800">{requestDetail.request_payload.topics_of_interest.join(', ')}</span>
                  </div>
                )}
                {requestDetail.request_payload?.desired_expertise?.length > 0 && (
                  <div>
                    <span className="text-gray-500">Desired Expertise:</span>{' '}
                    <span className="text-gray-800">{requestDetail.request_payload.desired_expertise.join(', ')}</span>
                  </div>
                )}
                {requestDetail.request_payload?.request_context && (
                  <div>
                    <span className="text-gray-500">Context:</span>{' '}
                    <span className="text-gray-800">{requestDetail.request_payload.request_context}</span>
                  </div>
                )}
                {requestDetail.staff_notes && (
                  <div>
                    <span className="text-gray-500">Staff Notes:</span>{' '}
                    <span className="text-gray-800">{requestDetail.staff_notes}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Channel selector */}
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Channel</label>
            <div className="flex gap-2">
              {CHANNELS.map((ch) => {
                const Icon = ch.icon;
                return (
                  <button
                    key={ch.value}
                    onClick={() => setChannel(ch.value)}
                    className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1 ${
                      channel === ch.value
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-3 h-3" /> {ch.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Template selector */}
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Template (optional)</label>
            <div className="flex gap-2">
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="flex-1 text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">No template</option>
                {[...templates].sort((a, b) => {
                  if (a.template_category === 'mentorship' && b.template_category !== 'mentorship') return -1;
                  if (b.template_category === 'mentorship' && a.template_category !== 'mentorship') return 1;
                  return 0;
                }).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.template_name} ({t.template_category})
                  </option>
                ))}
              </select>
              <button
                onClick={previewTemplate}
                disabled={!selectedTemplateId || previewLoading}
                className="px-3 py-2 text-xs bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
              >
                <FiFileText className="w-3 h-3" />
                {previewLoading ? 'Loading...' : 'Preview'}
              </button>
            </div>
          </div>

          {/* Subject (for email) */}
          {channel === 'email' && (
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500"
                placeholder="Email subject line..."
              />
            </div>
          )}

          {/* Message body */}
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Message</label>
            <textarea
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              rows={8}
              className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500"
              placeholder="Write your outreach message..."
            />
          </div>

          {/* Test mode toggle */}
          {channel === 'email' && (
            <label className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
                className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="text-xs text-amber-800">
                Test mode — send to <strong>cdgreaves@outlook.com</strong> instead of target
              </span>
            </label>
          )}

          {/* Send button */}
          <button
            onClick={sendOutreach}
            disabled={sending || !messageBody.trim()}
            className={`w-full px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5 ${
              testMode ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {sending ? (
              <>
                <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                Sending...
              </>
            ) : (
              <>
                <FiSend className="w-3.5 h-3.5" /> Send Outreach
              </>
            )}
          </button>
        </div>
      )}

      {/* Outreach History */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-gray-500 uppercase">
            Outreach History {outreachAttempts.length > 0 && `(${outreachAttempts.length})`}
          </p>
          <button onClick={fetchOutreach} disabled={outreachLoading} className="p-1 text-gray-400 hover:text-indigo-600">
            <FiRefreshCw className={`w-3 h-3 ${outreachLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {outreachAttempts.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No outreach attempts yet.</p>
        ) : (
          <div className="space-y-2">
            {outreachAttempts.map((attempt) => {
              const isExpanded = expandedAttempt === attempt.id;
              const ChannelIcon = CHANNELS.find((c) => c.value === attempt.channel)?.icon || FiMail;

              return (
                <div key={attempt.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedAttempt(isExpanded ? null : attempt.id)}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                        attempt.channel === 'email' ? 'bg-blue-100 text-blue-700' :
                        attempt.channel === 'linkedin' ? 'bg-sky-100 text-sky-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        <ChannelIcon className="w-2.5 h-2.5 inline mr-0.5" />
                        {attempt.channel}
                      </span>
                      <span className="text-xs text-gray-500">
                        {attempt.sent_at ? new Date(attempt.sent_at).toLocaleString() : 'Pending'}
                      </span>
                      {attempt.response_type && (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                          attempt.response_type === 'positive' ? 'bg-green-100 text-green-700' :
                          attempt.response_type === 'negative' ? 'bg-red-100 text-red-700' :
                          attempt.response_type === 'defer' ? 'bg-blue-100 text-blue-700' :
                          attempt.response_type === 'bounce' ? 'bg-gray-200 text-gray-600' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>{attempt.response_type}</span>
                      )}
                    </div>
                    {isExpanded ? <FiChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <FiChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-200 p-3 space-y-2">
                      {attempt.subject && (
                        <div>
                          <span className="text-[10px] text-gray-400 uppercase">Subject:</span>
                          <p className="text-xs text-gray-700">{attempt.subject}</p>
                        </div>
                      )}
                      {attempt.message_body && (
                        <div>
                          <span className="text-[10px] text-gray-400 uppercase">Message:</span>
                          <p className="text-xs text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">{attempt.message_body}</p>
                        </div>
                      )}
                      {attempt.response_notes && (
                        <div>
                          <span className="text-[10px] text-gray-400 uppercase">Response Notes:</span>
                          <p className="text-xs text-gray-700">{attempt.response_notes}</p>
                        </div>
                      )}

                      {/* Record Response */}
                      {!attempt.response_type && (
                        respondingTo === attempt.id ? (
                          <div className="bg-gray-50 rounded-lg p-2 space-y-2">
                            <p className="text-xs font-medium text-gray-700">Record Response</p>
                            <div className="flex flex-wrap gap-1">
                              {RESPONSE_TYPES.map((rt) => (
                                <button
                                  key={rt.value}
                                  onClick={() => setResponseType(rt.value)}
                                  className={`px-2 py-1 text-[10px] rounded-lg ${
                                    responseType === rt.value
                                      ? 'ring-2 ring-indigo-500 ' + rt.color
                                      : rt.color
                                  }`}
                                >
                                  {rt.label}
                                </button>
                              ))}
                            </div>
                            <textarea
                              value={responseNotes}
                              onChange={(e) => setResponseNotes(e.target.value)}
                              rows={2}
                              className="w-full text-xs border border-gray-300 rounded p-1.5"
                              placeholder="Notes about the response..."
                            />
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleRecordResponse(attempt.id)}
                                disabled={recordingResponse || !responseType}
                                className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                              >
                                {recordingResponse ? 'Saving...' : 'Save Response'}
                              </button>
                              <button
                                onClick={() => { setRespondingTo(null); setResponseType(''); setResponseNotes(''); }}
                                className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setRespondingTo(attempt.id)}
                            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                          >
                            Record Response
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OutreachPanel;
